import React from "react";
import { Error as ErrorIcon, KeyboardArrowRight } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  Skeleton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import type { UseQueryResult } from "@tanstack/react-query";
import { Loading } from "~/components/Loading";
import { renderQuery } from "~/components/QueryRenderer";
import { ErrorMessage } from "~/components/error-message";
import type {
  LogInstantTag,
  LogLevelTag,
  LogRangeTag,
  LogTag,
} from "~/domain/logs";
import {
  checkIsTimestampedLogTag,
  LogTagChip,
  pairTagWithType,
  useLogInstantTags,
  useLogLevelTags,
  useLogRangeTags,
} from "~/domain/logs";
import { SidebarHeader } from "~/layout";
import type { Log } from "~/lqs";
import {
  useActiveTag,
  useAreLogTagsShowing,
  useFormatPlaybackTimestamp,
  usePlayerActions,
  usePlayerLog,
  usePlayerParams,
} from "~/player";
import { ActiveTagSection } from "./active-tag-section";
import type { LabelManagementSectionProps } from "./label-management-section";

export function TaggingSidebar({
  renderLabelManagementSection,
}: {
  renderLabelManagementSection?: (
    props: LabelManagementSectionProps,
  ) => React.ReactNode;
}) {
  const readonly = renderLabelManagementSection == null;

  const { logId } = usePlayerParams();
  const playerLogQuery = usePlayerLog();

  const areLogTagsShowing = useAreLogTagsShowing();

  const playerActions = usePlayerActions();

  function handleToggleTags(): void {
    playerActions.toggleLogTags();
  }

  function handleClearActiveTag(): void {
    playerActions.clearActiveTag();
  }

  const logLevelTagsQuery = useLogLevelTags(playerLogQuery.data);
  const logInstantTagsQuery = useLogInstantTags(logId);
  const logRangeTagsQuery = useLogRangeTags(logId);

  const [activeTagQuery, activeTagSelected] = useActiveTag({ logId });

  let content;
  if (logId == null) {
    content = (
      <Alert severity="info" variant="filled" sx={{ mb: 2 }}>
        Choose a log in the log sidebar to begin tagging
      </Alert>
    );
  } else {
    content = renderQuery(playerLogQuery, {
      loading: <Loading type="circular" />,
      error: <ErrorMessage>Unable to load log. Cannot tag.</ErrorMessage>,
      success(log) {
        let tagsSection;
        if (activeTagSelected) {
          tagsSection = (
            <Box sx={{ marginBlockEnd: 3 }}>
              {renderQuery(activeTagQuery, {
                loading: <Loading type="circular" />,
                error: (
                  <ErrorMessage disableTypography>
                    <ErrorMessage.Paragraph>
                      Unable to load tag.
                    </ErrorMessage.Paragraph>
                    <Button
                      color="error"
                      variant="outlined"
                      fullWidth
                      onClick={handleClearActiveTag}
                    >
                      Return to Tags
                    </Button>
                  </ErrorMessage>
                ),
                success: (tag) => (
                  <ActiveTagSection log={log} tag={tag} readonly={readonly} />
                ),
              })}
            </Box>
          );
        } else {
          tagsSection = (
            <>
              <Typography variant="overline" gutterBottom>
                Log Tags
              </Typography>
              {renderTagsQuery({
                query: logLevelTagsQuery,
                errorText: "Unable to load log-level tags",
                emptyText: "No log-level tags",
                renderTags: (tags) => <TagList log={log} tags={tags} />,
              })}
              <Typography variant="overline" gutterBottom>
                Log Instant Tags
              </Typography>
              {renderTagsQuery({
                query: logInstantTagsQuery,
                errorText: "Unable to load log instant tags",
                emptyText: "No log instant tags",
                renderTags: (tags) => <TagList log={log} tags={tags} />,
              })}
              <Typography variant="overline" gutterBottom>
                Log Range Tags
              </Typography>
              {renderTagsQuery({
                query: logRangeTagsQuery,
                errorText: "Unable to load log range tags",
                emptyText: "No log range tags",
                renderTags: (tags) => <TagList log={log} tags={tags} />,
              })}
            </>
          );
        }

        return (
          <>
            <FormControlLabel
              sx={{ justifyContent: "space-between", ml: 0 }}
              control={
                <Switch
                  checked={areLogTagsShowing}
                  onChange={handleToggleTags}
                />
              }
              label="Show timestamped log tags"
              labelPlacement="start"
            />
            <Divider sx={{ my: 2 }} />
            {tagsSection}
            {!readonly && (
              <>
                <Divider sx={{ mb: 2 }} />
                {renderLabelManagementSection({
                  log,
                  logLevelTagsQuery,
                  logInstantTagsQuery,
                  logRangeTagsQuery,
                })}
              </>
            )}
          </>
        );
      },
    });
  }

  return (
    <>
      <SidebarHeader title="Tagging" />
      <Stack>{content}</Stack>
    </>
  );
}

function renderTagsQuery<TTag extends LogTag>({
  query,
  errorText,
  emptyText,
  renderTags,
}: {
  query: UseQueryResult<ReadonlyArray<TTag>>;
  errorText: string;
  emptyText: React.ReactNode;
  renderTags: (tags: ReadonlyArray<TTag>) => React.ReactNode;
}) {
  return (
    <Stack spacing={1} sx={{ marginBlockEnd: 2 }}>
      {renderQuery(query, {
        loading: [1, 2, 3].map((key) => (
          <Skeleton
            key={key}
            variant="rectangular"
            width="8ch"
            height={32}
            sx={{ borderRadius: 4 }}
          />
        )),
        error: (
          <>
            <ErrorIcon color="error" />
            <Typography>{errorText}</Typography>
          </>
        ),
        success(tags) {
          return tags.length === 0 ? (
            <Typography sx={{ fontStyle: "italic", height: 32 }}>
              {emptyText}
            </Typography>
          ) : (
            renderTags(tags)
          );
        },
      })}
    </Stack>
  );
}

function TagList({
  log,
  tags,
}: {
  log: Log;
  tags:
    | ReadonlyArray<LogLevelTag>
    | ReadonlyArray<LogInstantTag>
    | ReadonlyArray<LogRangeTag>;
}) {
  return (
    <List disablePadding>
      {tags.map((tag) => (
        <TagListItem key={tag.id} log={log} tag={tag} />
      ))}
    </List>
  );
}

function TagListItem({ log, tag }: { log: Log; tag: LogTag }) {
  const formatPlaybackTimestamp = useFormatPlaybackTimestamp();

  const playerActions = usePlayerActions();

  function handleSetActiveTag(): void {
    playerActions.setActiveTag(tag);
  }

  function createSeekHandler(timestamp: bigint) {
    return function handleSeek(): void {
      playerActions.seek(timestamp);
    };
  }

  const tagWithType = pairTagWithType(tag);

  return (
    <ListItem
      sx={{
        padding: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          marginBlockStart: 0.5,
          marginBlockEnd: 1,
        }}
      >
        <LogTagChip
          log={log}
          {...tagWithType}
          readonly
          formatTimestamp={formatPlaybackTimestamp}
          onClick={
            checkIsTimestampedLogTag(tag)
              ? createSeekHandler(tag.startTime)
              : undefined
          }
          useHashColor
        />
        <Tooltip title="Show tag details">
          <IconButton onClick={handleSetActiveTag}>
            <KeyboardArrowRight />
          </IconButton>
        </Tooltip>
      </Stack>
    </ListItem>
  );
}
